import React, {useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useClickOutside} from '../../hooks/useClickOutside';
import {PushToggle} from '../UI/PushToggle';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faBell,
    faHourglassHalf,
    faMoneyBillTransfer,
    faUserPlus,
    faRightLeft,
    faCircleCheck,
    faEnvelope,
    faPenToSquare,
    faCircleExclamation,
    faReceipt,
    faDoorOpen,
} from '@fortawesome/free-solid-svg-icons';
import {AppNotification, NotificationKind} from '../../types/notification';

const KIND_STYLE: Record<NotificationKind, { icon: typeof faBell; tone: string }> = {
    review: {icon: faHourglassHalf, tone: 'bg-amber-500/15 text-amber-600 dark:text-amber-300'},
    payment: {icon: faMoneyBillTransfer, tone: 'bg-blue-500/15 text-blue-600 dark:text-blue-300'},
    registration: {icon: faUserPlus, tone: 'bg-slate-500/15 text-slate-600 dark:text-slate-300'},
    handover: {icon: faRightLeft, tone: 'bg-rose-500/15 text-rose-600 dark:text-rose-300'},
    emailChange: {icon: faEnvelope, tone: 'bg-violet-500/15 text-violet-600 dark:text-violet-300'},
    // Phía người thuê - cùng bảng màu với huy hiệu trạng thái ở trang Phòng của tôi
    remind: {icon: faPenToSquare, tone: 'bg-slate-500/15 text-slate-600 dark:text-slate-300'},
    rejected: {icon: faCircleExclamation, tone: 'bg-rose-500/15 text-rose-600 dark:text-rose-300'},
    invoice: {icon: faReceipt, tone: 'bg-violet-500/15 text-violet-600 dark:text-violet-300'},
    paid: {icon: faCircleCheck, tone: 'bg-slate-500/15 text-slate-600 dark:text-slate-300'},
    moveOut: {icon: faDoorOpen, tone: 'bg-amber-500/15 text-amber-600 dark:text-amber-300'},
};

interface NotificationBellProps {
    notifications: AppNotification[];
    /**
     * Số trên chấm đỏ. Mặc định là số mục - đúng cho chủ nhà, nơi mọi mục đều là
     * việc chưa làm. Người thuê truyền số mục CHƯA XEM: danh sách của họ có cả
     * mục đã xong (đã thu tiền), để đếm hết thì chấm đỏ sáng tới cuối tháng.
     */
    badgeCount?: number;
    heading?: string;
    emptyText?: string;
    /** Gọi mỗi lần mở bảng - người thuê dùng để đánh dấu đã xem */
    onOpen?: () => void;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
    notifications,
    badgeCount,
    heading = 'Việc đang chờ',
    emptyText = 'Không còn việc nào đang chờ',
    onOpen,
}) => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const total = notifications.length;
    const count = badgeCount ?? total;

    useClickOutside(containerRef, isOpen, () => setIsOpen(false));

    const toggle = () => {
        setIsOpen(open => {
            if (!open) onOpen?.();
            return !open;
        });
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                onClick={toggle}
                className="relative w-10 h-10 rounded-lg flex items-center justify-center
                    text-slate-600 dark:text-slate-300 hover:bg-white/60 dark:hover:bg-white/10 transition-colors"
                aria-label={count > 0 ? `${heading} (${count})` : heading}
                aria-expanded={isOpen}
            >
                <FontAwesomeIcon icon={faBell} className="w-5 h-5"/>
                {count > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1
                        rounded-full bg-rose-500 text-white text-[10px] font-bold
                        flex items-center justify-center border-2 border-white dark:border-slate-900">
                        {count > 9 ? '9+' : count}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 z-50 w-[min(22rem,calc(100vw-2rem))]
                    glass-panel overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/40 dark:border-white/10">
                        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                            {heading} {total > 0 && <span className="text-rose-500">({total})</span>}
                        </h3>
                    </div>

                    <div className="max-h-80 overflow-y-auto scrollbar-thin divide-y divide-white/40 dark:divide-white/10">
                        {total === 0 ? (
                            <div className="px-4 py-8 text-center">
                                <FontAwesomeIcon icon={faCircleCheck} className="text-2xl text-slate-500 mb-2"/>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    {emptyText}
                                </p>
                            </div>
                        ) : (
                            notifications.map(notification => {
                                const style = KIND_STYLE[notification.kind];
                                const to = notification.to;
                                const body = (
                                    <>
                                        <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${style.tone}`}>
                                            <FontAwesomeIcon icon={style.icon} className="text-sm"/>
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-sm font-medium text-slate-800 dark:text-white">
                                                {notification.title}
                                            </span>
                                            <span className="block text-xs text-slate-500 dark:text-slate-400">
                                                {notification.detail}
                                            </span>
                                        </span>
                                    </>
                                );

                                // Không có đích đến thì đừng làm nút: bấm vào không
                                // đi đâu cả, mà con trỏ/hiệu ứng hover lại hứa là có.
                                return to ? (
                                    <button
                                        key={notification.id}
                                        onClick={() => {
                                            navigate(to);
                                            setIsOpen(false);
                                        }}
                                        className="w-full flex items-start gap-3 px-4 py-3 text-left
                                            hover:bg-white/50 dark:hover:bg-white/5 transition-colors"
                                    >
                                        {body}
                                    </button>
                                ) : (
                                    <div key={notification.id} className="flex items-start gap-3 px-4 py-3">
                                        {body}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Bật thông báo đẩy - ĐẶT NGAY CẠNH danh sách thông báo.
                        Trước đây nút này chỉ có trong Cài đặt, mà người thuê
                        không vào được Cài đặt, còn chủ nhà thì phải biết trước
                        là có nó mới đi tìm. Kết quả: không máy nào từng đăng ký,
                        và người dùng ngồi đợi một thông báo hệ thống không bao
                        giờ tới trong khi chuông trong app vẫn sáng bình thường.

                        Chuông là chỗ người ta nghĩ tới thông báo, nên câu trả
                        lời cho "sao đóng app là hết báo" phải nằm ở đây. Chuông
                        này dùng chung cho AppHeader (chủ nhà) và TenantLayout
                        (người thuê) nên đặt một lần là cả hai vai đều thấy. */}
                    <div className="px-4 py-3 border-t border-white/40 dark:border-white/10">
                        <PushToggle/>
                    </div>
                </div>
            )}
        </div>
    );
};
